import { createContext, type ReactNode, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dePathToEn, enPathToDe } from './routes';
import type { Translation } from './translations';
import { de } from './translations/de';
import { en } from './translations/en';

export type SupportedLang = 'de' | 'en';

const translations: Record<SupportedLang, Translation> = { de, en };

export interface LanguageContextValue {
  lang: SupportedLang;
  t: Translation;
  switchLang: () => void;
}

export const LanguageContext = createContext<LanguageContextValue | null>(null);

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const lang: SupportedLang = location.pathname.startsWith('/en') ? 'en' : 'de';

  const t = translations[lang];

  const switchLang = useCallback(() => {
    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(de|en)(?=\/|$)/, '') || '/';

    // Translate the stripped path through the i18n route map so slug-
    // differing routes (e.g. /datenschutz ↔ /privacy, /impressum ↔ /imprint,
    // /ki-agenten ↔ /ai-agents) flip correctly instead of producing
    // non-existent paths like /de/privacy.
    const translated =
      pathWithoutLang === '/'
        ? '/'
        : lang === 'de'
          ? dePathToEn(pathWithoutLang)
          : enPathToDe(pathWithoutLang);

    const target = lang === 'de' ? '/en' : '/de';
    navigate(translated === '/' ? target : `${target}${translated}`);
  }, [lang, location.pathname, navigate]);

  const value = useMemo<LanguageContextValue>(
    () => ({ lang, t, switchLang }),
    [lang, t, switchLang],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}
